import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, getDuration, List, Text, useResponsive } from '@common-fe/common-fe';

import { FULL_MONTH_DATE_FORMAT } from '@/common/constants';
import { useInvestmentsHoldings } from '@/modules/investments/hooks';
import { CategoryStockItem } from '@/modules/investments/hooks/useInvestmentsHoldings';

import FundsDetailsModal from '../FundsDetailsModal';
import { useGetInvestorAccountId } from '../hook';
import { useInvestmentTabStore } from '../stores';

import FundsItem from './FundsItem';
const SHOW_MORE_COUNT = 10;
export type FundPayload = CategoryStockItem;
interface Props {}
const Funds: React.FC<Props> = () => {
  const { isMobile } = useResponsive();
  const [mobileModalFundId, setMobileModalFundId] = useState<null | string>(null);

  const highlightedFund = useInvestmentTabStore((state) => state.highlightedFund);
  const setHighlightedFund = useInvestmentTabStore((state) => state.setHighlightedFund);

  useEffect(() => {
    if (highlightedFund) {
      const targetElement = document.getElementById(highlightedFund);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      setTimeout(() => {
        setHighlightedFund('');
      }, getDuration({ seconds: 2 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedFund]);
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);
  const accountId = useGetInvestorAccountId();
  const { isHoldingLoading, fundsGroupedByCategories, lastDateUpdate } = useInvestmentsHoldings(accountId);
  const showMoreItems = useMemo(() => {
    if (fundsGroupedByCategories.length > SHOW_MORE_COUNT) {
      return fundsGroupedByCategories.length - SHOW_MORE_COUNT;
    }
    return 0;
  }, [fundsGroupedByCategories.length]);
  const foundMobileModalItem = useMemo(() => {
    if (mobileModalFundId) {
      return fundsGroupedByCategories.find((item) => item.id === mobileModalFundId);
    }
    return null;
  }, [fundsGroupedByCategories, mobileModalFundId]);
  const list = useMemo(() => {
    if (isExpanded) {
      return fundsGroupedByCategories;
    }
    return [...fundsGroupedByCategories].slice(0, SHOW_MORE_COUNT);
  }, [fundsGroupedByCategories, isExpanded]);
  const handleToggleExpand = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded]);

  const handleOpenModal = useCallback(
    (id: string) => () => {
      setMobileModalFundId(id);
    },
    []
  );

  return (
    <Box data-testid="funds" direction="column" fill>
      <Box id="funds" direction="row" align="center" justify="between" height="40px" margin={{ bottom: 's' }}>
        <Text data-testid="funds_total" weight={700}>
          {`${t('Funds')}: ${fundsGroupedByCategories.length || 0}`}
        </Text>
        {lastDateUpdate && (
          <Text weight={400} color="textSecondary">
            {`${t('Date as of')} ${lastDateUpdate.format(FULL_MONTH_DATE_FORMAT)}`}
          </Text>
        )}
      </Box>
      {!isMobile && (
        <List.Header>
          <List.Header.Item flex={12}>{t('Holding name')}</List.Header.Item>
          <List.Header.Item flex={4}>{t('Category')}</List.Header.Item>

          <List.Header.Item flex={2}>{t('Shares')}</List.Header.Item>
          <List.Header.Item flex={3}>{t('Share price')}</List.Header.Item>
          <List.Header.Item flex={2}>{t('Election')}</List.Header.Item>
          <List.Header.Item flex={3}>{t('Balance')}</List.Header.Item>
          <Box width="72px" />
        </List.Header>
      )}

      {isHoldingLoading ? (
        <List.Preloader count={4} testId="funds_loading" />
      ) : (
        <Box direction="column">
          <FundsDetailsModal item={foundMobileModalItem} onClose={() => setMobileModalFundId(null)} />
          {list.length === 0 && <List.Placeholder text=" " />}

          {list.map(({ key, ...item }) => (
            <Box key={key}>
              <FundsItem
                key={item.id || key}
                onClick={handleOpenModal(item.id)}
                isHighlighted={key === highlightedFund}
                {...item}
              />
            </Box>
          ))}

          {!!showMoreItems && (
            <Box direction="column" align="center" justify="center" margin={{ top: 'l' }}>
              <AppButton testId="funds_show-more" onClick={handleToggleExpand}>
                {isExpanded ? t('Show Less') : `${t('Show More')} (${showMoreItems})`}
              </AppButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Funds;
